import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../../shared/toast/toast.service';
import { getApiErrorMessage } from '../utils/api-error.util';

const AUTH_ENDPOINT_PATTERN = /\/auth\/(?:login|refresh|logout)(?:\?|$)/i;
const TOKEN_ERROR_CODES = new Set([
  'token_expired',
  'invalid_token',
  'token_invalid',
  'unauthorized',
]);

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINT_PATTERN.test(url);
}

function getErrorCode(error: HttpErrorResponse): string {
  return String(error.error?.errorCode ?? '').toLowerCase();
}

function retryWithUpdatedToken(
  req: HttpRequest<unknown>,
  next: (r: HttpRequest<unknown>) => ReturnType<HttpInterceptorFn>,
  token: string,
) {
  return next(
    req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    }),
  );
}

function refreshAndRetry(
  req: HttpRequest<unknown>,
  next: (r: HttpRequest<unknown>) => ReturnType<HttpInterceptorFn>,
  authService: AuthService,
  failMessage: string,
  originalError?: HttpErrorResponse,
) {
  return authService.extendSession().pipe(
    switchMap((success) => {
      const refreshedToken = authService.getToken();
      if (success && refreshedToken) {
        return retryWithUpdatedToken(req, next, refreshedToken);
      }
      if (authService.hasExpiredAccessToken()) {
        authService.handleSessionExpired(failMessage);
      }
      return throwError(
        () => originalError ?? new HttpErrorResponse({ status: 401 }),
      );
    }),
  );
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const toastService = inject(ToastService);

  const isAuthRoute = isAuthEndpoint(req.url);
  const token = authService.getToken();

  // Access JWT expired — silently refresh (even if the warning modal is open).
  if (token && !isAuthRoute && authService.hasExpiredAccessToken()) {
    if (authService.canRefreshSession()) {
      return refreshAndRetry(
        req,
        next,
        authService,
        'Please sign in again.',
      );
    }

    authService.handleSessionExpired('Please sign in again.');
    return throwError(() => new HttpErrorResponse({ status: 401 }));
  }

  const shouldAttachToken = !!token && !isAuthRoute;
  const authReq = shouldAttachToken
    ? req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      })
    : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      const errorCode = getErrorCode(err);

      if (
        !isAuthRoute &&
        err.status === 401 &&
        (TOKEN_ERROR_CODES.has(errorCode) || errorCode === '')
      ) {
        if (authService.canRefreshSession()) {
          return refreshAndRetry(
            req,
            next,
            authService,
            getApiErrorMessage(err, 'Please sign in again.'),
            err,
          );
        }

        authService.handleSessionExpired(
          getApiErrorMessage(err, 'Please sign in again.'),
        );
      }

      if (!isAuthRoute && err.status === 403) {
        toastService.error(
          'Access Denied',
          getApiErrorMessage(
            err,
            'You do not have permission to access this resource.',
          ),
        );
      }

      return throwError(() => err);
    }),
  );
};
